import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useMediaQuery } from '@mui/material';
import { createPortal } from 'react-dom';
import CloseIcon from '@mui/icons-material/Close';
import RemoveIcon from '@mui/icons-material/Remove';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import FilterNoneIcon from '@mui/icons-material/FilterNone';

// ── หน้าต่างแบบ Windows OS: ลากย้ายได้, ปรับขนาดได้, ย่อลง taskbar ได้, กากบาทปิดจริง ──
// เท่ากับ breakpoints.down('md') ของ MUI (จอ < 900px) — ต่ำกว่านี้แสดงหน้าต่างเต็มจอ
const MOBILE_QUERY = '(max-width: 899.95px)';
const MIN_WIDTH = 420;
const MIN_HEIGHT = 320;
const EDGE = 6; // ความหนาโซนจับขอบสำหรับ resize (px)
const CORNER = 16; // ขนาดโซนจับมุม (px)

const WIN_BTN = {
  width: 28,
  height: 24,
  border: 'none',
  background: 'transparent',
  color: '#fff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  borderRadius: 3,
};

const TASKBAR_CHIP = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 14px',
  borderRadius: 6,
  border: '1px solid transparent',
  background: 'rgba(255,255,255,0.08)',
  cursor: 'pointer',
  fontSize: '0.8rem',
  fontWeight: 600,
  color: '#e2e8f0',
  maxWidth: 220,
  transition: 'background 0.12s ease, transform 0.12s ease',
};

const TASKBAR_CHIP_DOT = {
  width: 8,
  height: 8,
  borderRadius: '50%',
  background: '#60a5fa',
  boxShadow: '0 0 0 3px rgba(96,165,250,0.2)',
  flexShrink: 0,
};

// ── Taskbar ร่วม แบบเดียวกับ Windows: แถบเต็มความกว้างจอ ติดขอบล่าง ทุก WindowFrame ที่ minimize
// อยู่จะ portal chip ของตัวเองเข้า container เดียวกันนี้ ให้ flex เรียงเป็นแถวในแถบเดียวกันเสมอ
// (กันไม่ให้ไปวางซ้อนทับกันคนละที่) — แถบจะโผล่มาเองเมื่อมีอย่างน้อย 1 หน้าต่างถูกย่อ และหายไปเมื่อไม่มี ──
let taskbarRootEl = null;
// มือถือไม่มี sidebar กว้าง 320px ให้เว้น → แถบ taskbar เริ่มจากขอบซ้ายจอ
const taskbarLeft = () => (window.matchMedia(MOBILE_QUERY).matches ? '0px' : '320px');

function getTaskbarRoot() {
  if (taskbarRootEl && document.body.contains(taskbarRootEl)) {
    taskbarRootEl.style.left = taskbarLeft();
    return taskbarRootEl;
  }
  taskbarRootEl = document.createElement('div');
  taskbarRootEl.id = 'window-frame-taskbar';
  Object.assign(taskbarRootEl.style, {
    position: 'fixed',
    // 320px = ความกว้าง sidebar หลัก (primary rail 80 + secondary "sub menu" rail 240
    // ใน MainSidebar.js) — เริ่มแถบ taskbar ต่อจาก sidebar ไปจนสุดขอบขวาจอ ไม่ทับ sidebar
    // ต้องใส่หน่วย 'px' เป็น string เพราะ set ผ่าน .style ตรงๆ (ไม่ใช่ JSX) — เลขเปล่าไม่มีหน่วย
    // เป็นค่า invalid ที่เบราว์เซอร์จะเมิน ทำให้ left กลายเป็น auto แถบเลยหดไปมุมขวาแทนที่จะยาวเต็ม
    left: taskbarLeft(),
    right: '0px',
    bottom: '0px',
    zIndex: 1300,
    display: 'none',
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    background: 'rgba(15,23,42,0.9)',
    backdropFilter: 'blur(12px)',
    borderTop: '1px solid rgba(255,255,255,0.08)',
    boxShadow: '0 -6px 24px rgba(0,0,0,0.25)',
  });
  document.body.appendChild(taskbarRootEl);
  // โชว์แถบเฉพาะตอนมี chip อย่างน้อย 1 อัน — ซ่อนไปเลยตอนไม่มีหน้าต่างไหนถูกย่อ
  const syncVisibility = () => {
    taskbarRootEl.style.display = taskbarRootEl.childElementCount > 0 ? 'flex' : 'none';
  };
  new MutationObserver(syncVisibility).observe(taskbarRootEl, { childList: true });
  return taskbarRootEl;
}

export default function WindowFrame({
  open,
  title,
  subtitle,
  onClose,
  children,
  defaultWidth = 1100,
  defaultHeight = 720,
  taskbarLabel,
  // เปิดหลายหน้าต่างพร้อมกัน (เช่น "ตรวจสอบเวลาเข้างาน" หลายแถว) — แบ่งความกว้างจอเป็นช่อง
  // ๆ ตามจำนวนหน้าต่างทั้งหมด (tileCount) แล้ววางหน้าต่างนี้ในช่องที่ tileIndex กันไม่ให้ทับกันเลย
  // (ไม่ใช่แค่เยื้องตำแหน่งเฉยๆ) — ค่า default (index 0, count 1) = จัดกึ่งกลางจอปกติเหมือนเดิม
  tileIndex = 0,
  tileCount = 1,
}) {
  const [minimized, setMinimized] = useState(false);
  const [maximized, setMaximized] = useState(false);
  // มือถือ: หน้าต่างลอย (ลาก/ปรับขนาดด้วยเมาส์) ใช้ไม่ได้บนจอสัมผัสและกว้างเกินจอ → แสดงเต็มจอเสมอ ไม่มีปุ่มขยาย
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const isMax = maximized || isMobile;
  const [pos, setPos] = useState(null);
  const [size, setSize] = useState({ width: defaultWidth, height: defaultHeight });
  const dragRef = useRef(null);
  const resizeRef = useRef(null);
  const preMaximizeRef = useRef(null);

  // จัดตำแหน่ง — แบ่งความกว้างจอเป็น tileCount ช่องเท่าๆ กัน วางหน้าต่างนี้กึ่งกลางช่องที่ tileIndex
  // (ถ้ามีหน้าต่างเดียว tileCount=1 ก็คือกึ่งกลางจอทั้งจอเหมือนเดิม) — ต้อง re-tile ใหม่ทุกครั้งที่
  // tileIndex/tileCount เปลี่ยน (ไม่ใช่แค่ตอนเปิดครั้งแรก) ไม่งั้นหน้าต่างที่เปิดอยู่ก่อนจะยังกินพื้นที่
  // เต็มจอเดิมค้างไว้ พอมีหน้าต่างที่ 2 เปิดมาแบ่งครึ่งจอ จะไปทับกับหน้าต่างแรกที่ไม่ได้ resize ตาม
  useEffect(() => {
    if (open) {
      const count = Math.max(1, tileCount);
      const slotW = window.innerWidth / count;
      const w = Math.min(defaultWidth, Math.max(MIN_WIDTH, slotW - 24));
      const h = Math.min(defaultHeight, window.innerHeight - 80);
      const slotX = tileIndex * slotW;
      setPos({
        x: Math.max(0, slotX + (slotW - w) / 2),
        y: Math.max(0, (window.innerHeight - h) / 2 - 20),
      });
      setSize({ width: w, height: h });
    } else {
      setMinimized(false);
      setMaximized(false);
      setPos(null);
    }
  }, [open, tileIndex, tileCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleMaximize = () => {
    if (maximized) {
      // คืนขนาด/ตำแหน่งก่อนขยายเต็มจอ
      if (preMaximizeRef.current) {
        setPos(preMaximizeRef.current.pos);
        setSize(preMaximizeRef.current.size);
      }
      setMaximized(false);
    } else {
      preMaximizeRef.current = { pos, size };
      setMaximized(true);
    }
  };

  const onDragMove = useCallback((e) => {
    if (!dragRef.current) return;
    const { startX, startY, origPos } = dragRef.current;
    setPos({
      x: origPos.x + (e.clientX - startX),
      y: Math.max(0, origPos.y + (e.clientY - startY)),
    });
  }, []);
  const onDragEnd = useCallback(() => {
    dragRef.current = null;
    window.removeEventListener('mousemove', onDragMove);
    window.removeEventListener('mouseup', onDragEnd);
  }, [onDragMove]);
  const onDragStart = (e) => {
    if (isMax || e.button !== 0) return;
    e.preventDefault();
    dragRef.current = { startX: e.clientX, startY: e.clientY, origPos: pos };
    window.addEventListener('mousemove', onDragMove);
    window.addEventListener('mouseup', onDragEnd);
  };

  // resize จากขอบ/มุมไหนก็ได้ — dir คือชุดตัวอักษรทิศ เช่น 'e', 'w', 'n', 's', 'ne', 'nw', 'se', 'sw'
  const onResizeMove = useCallback((e) => {
    if (!resizeRef.current) return;
    const { dir, startX, startY, origSize, origPos } = resizeRef.current;
    const dx = e.clientX - startX;
    const dy = e.clientY - startY;

    let newWidth = origSize.width;
    let newHeight = origSize.height;
    let newX = origPos.x;
    let newY = origPos.y;

    if (dir.includes('e')) {
      newWidth = Math.max(MIN_WIDTH, origSize.width + dx);
    }
    if (dir.includes('s')) {
      newHeight = Math.max(MIN_HEIGHT, origSize.height + dy);
    }
    if (dir.includes('w')) {
      newWidth = Math.max(MIN_WIDTH, origSize.width - dx);
      newX = origPos.x + (origSize.width - newWidth);
    }
    if (dir.includes('n')) {
      newHeight = Math.max(MIN_HEIGHT, origSize.height - dy);
      newY = Math.max(0, origPos.y + (origSize.height - newHeight));
    }

    setSize({ width: newWidth, height: newHeight });
    setPos({ x: newX, y: newY });
  }, []);
  const onResizeEnd = useCallback(() => {
    resizeRef.current = null;
    window.removeEventListener('mousemove', onResizeMove);
    window.removeEventListener('mouseup', onResizeEnd);
  }, [onResizeMove]);
  const onResizeStart = (dir) => (e) => {
    if (isMax) return;
    e.preventDefault();
    e.stopPropagation();
    resizeRef.current = { dir, startX: e.clientX, startY: e.clientY, origSize: size, origPos: pos };
    window.addEventListener('mousemove', onResizeMove);
    window.addEventListener('mouseup', onResizeEnd);
  };

  if (!open || !pos) return null;

  // สำคัญ: ตอน minimize ต้อง "ซ่อนด้วย CSS" (display:none) ไม่ใช่ไม่ render {children} เลย
  // เพราะถ้าไม่ render children (เช่น HrReportV2) จะถูก React unmount ทิ้งจริงๆ ทำให้ข้อมูล/state
  // ที่โหลดไว้หายหมด พอกดคืนหน้าต่างต้องโหลดข้อมูลใหม่ทั้งหมด — เก็บ children ให้ mount ค้างไว้เสมอ
  // ("แคช" ข้อมูลไว้ตามที่ขอ) แล้วโชว์ปุ่ม taskbar chip เพิ่มเข้ามาแทนตอน minimize
  return (
    <>
      {minimized &&
        createPortal(
          <button
            style={TASKBAR_CHIP}
            onClick={() => setMinimized(false)}
            title={`คืนหน้าต่าง: ${taskbarLabel || title}`}
            onMouseEnter={(e) => {
              e.currentTarget.style.background = 'rgba(255,255,255,0.18)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = TASKBAR_CHIP.background;
            }}
          >
            <span style={TASKBAR_CHIP_DOT} />
            <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {taskbarLabel || title}
            </span>
          </button>,
          getTaskbarRoot(),
        )}
      {createPortal(
        <div
          style={
            minimized
              ? { display: 'none' }
              : isMax
              ? {
                  position: 'fixed',
                  inset: 0,
                  width: '100vw',
                  height: '100vh',
                  background: '#fff',
                  borderRadius: 0,
                  boxShadow: 'none',
                  display: 'flex',
                  flexDirection: 'column',
                  zIndex: 1300,
                  overflow: 'hidden',
                  border: 'none',
                }
              : {
                  position: 'fixed',
                  left: pos.x,
                  top: pos.y,
                  width: size.width,
                  height: size.height,
                  background: '#fff',
                  borderRadius: 8,
                  boxShadow: '0 25px 50px -12px rgba(0,0,0,0.35)',
                  display: 'flex',
                  flexDirection: 'column',
                  zIndex: 1300,
                  overflow: 'hidden',
                  border: '1px solid #cbd5e1',
                }
          }
        >
      {/* Title bar — ลากที่นี่เพื่อย้ายหน้าต่าง, ดับเบิลคลิกเพื่อขยาย/คืนขนาด */}
      <div
        onMouseDown={onDragStart}
        onDoubleClick={toggleMaximize}
        style={{
          cursor: isMax ? 'default' : 'move',
          userSelect: 'none',
          background: '#1e293b',
          color: '#fff',
          padding: '8px 8px 8px 14px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexShrink: 0,
          gap: 8,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: '0.85rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {title}
          </div>
          {subtitle && (
            <div style={{ fontSize: '0.7rem', opacity: 0.75, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {subtitle}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={() => setMinimized(true)}
            title="ย่อหน้าต่างลง taskbar"
            style={WIN_BTN}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <RemoveIcon style={{ fontSize: 16 }} />
          </button>
          {!isMobile && (
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={toggleMaximize}
            title={maximized ? 'คืนขนาดหน้าต่าง' : 'ขยายเต็มจอ'}
            style={WIN_BTN}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            {maximized ? <FilterNoneIcon style={{ fontSize: 13 }} /> : <CropSquareIcon style={{ fontSize: 14 }} />}
          </button>
          )}
          <button
            onMouseDown={(e) => e.stopPropagation()}
            onClick={onClose}
            title="ปิดหน้าต่าง"
            style={WIN_BTN}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#dc2626'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
          >
            <CloseIcon style={{ fontSize: 16 }} />
          </button>
        </div>
      </div>

      {/* เนื้อหา */}
      <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {children}
      </div>

      {/* ขอบ 4 ด้าน + มุม 4 มุม — วางเมาส์ที่ขอบไหนก็ปรับขนาดจากขอบนั้นได้ เหมือนหน้าต่าง Windows (ซ่อนตอนขยายเต็มจอ) */}
      {!isMax && (
        <>
          {/* ขอบบน/ล่าง/ซ้าย/ขวา */}
          <div onMouseDown={onResizeStart('n')} style={{ position: 'absolute', top: 0, left: EDGE, right: EDGE, height: EDGE, cursor: 'ns-resize' }} />
          <div onMouseDown={onResizeStart('s')} style={{ position: 'absolute', bottom: 0, left: EDGE, right: EDGE, height: EDGE, cursor: 'ns-resize' }} />
          <div onMouseDown={onResizeStart('w')} style={{ position: 'absolute', left: 0, top: EDGE, bottom: EDGE, width: EDGE, cursor: 'ew-resize' }} />
          <div onMouseDown={onResizeStart('e')} style={{ position: 'absolute', right: 0, top: EDGE, bottom: EDGE, width: EDGE, cursor: 'ew-resize' }} />
          {/* มุมทั้ง 4 — ทับซ้อนขอบตรงหัวมุม ให้ resize ทแยงได้ */}
          <div onMouseDown={onResizeStart('nw')} style={{ position: 'absolute', top: 0, left: 0, width: CORNER, height: CORNER, cursor: 'nwse-resize' }} />
          <div onMouseDown={onResizeStart('ne')} style={{ position: 'absolute', top: 0, right: 0, width: CORNER, height: CORNER, cursor: 'nesw-resize' }} />
          <div onMouseDown={onResizeStart('sw')} style={{ position: 'absolute', bottom: 0, left: 0, width: CORNER, height: CORNER, cursor: 'nesw-resize' }} />
          <div
            onMouseDown={onResizeStart('se')}
            title="ลากเพื่อปรับขนาด"
            style={{ position: 'absolute', bottom: 0, right: 0, width: CORNER, height: CORNER, cursor: 'nwse-resize' }}
          >
            <svg width={CORNER} height={CORNER} style={{ display: 'block' }}>
              <path d="M14 4 L4 14 M17 8 L8 17 M17 13 L13 17" stroke="#94a3b8" strokeWidth="1.5" />
            </svg>
          </div>
        </>
      )}
        </div>,
        document.body,
      )}
    </>
  );
}
